import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Navbar,
  Nav,
  Container,
  Offcanvas,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocalePath } from "../i18n/hooks";
import LanguageSwitcher from "./LanguageSwitcher";

type NavLinkItem = {
  key: string;
  label: string;
  to?: string;
  href?: string;
};

function Navigation() {
  const { t } = useTranslation("common");
  const localePath = useLocalePath();
  const location = useLocation();

  // Offcanvas (mobile)
  const [showOffcanvas, setShowOffcanvas] = useState(false);

  // Close menus on route change
  useEffect(() => {
    setShowOffcanvas(false);
  }, [location.pathname]);

  const topLinks = useMemo<NavLinkItem[]>(
    () => [
      {
        key: "templates",
        label: t("nav.templates", { defaultValue: "Templates" }),
        to: "/templates",
      },
      {
        key: "contact",
        label: t("nav.contact"),
        href: "https://www.linkedin.com/company/see3eye",
      },
    ],
    [t]
  );

  return (
    <>
      <Navbar bg="light" expand="lg" sticky="top" className="site-banner nav-mega">
        <Container>
          <Navbar.Brand
            as={Link}
            to="/"
            className="d-flex align-items-center gap-2"
          >
            <img
              src="/3eye.png"
              alt={t("site.name")}
              height="40"
              className="d-inline-block"
            />
            {/* <span className="fw-bold">{t("site.name")}</span> */}
          </Navbar.Brand>

          {/* Mobile hamburger (shows Offcanvas) */}
          <Navbar.Toggle
            aria-controls="site-nav-offcanvas"
            className="d-lg-none"
            onClick={() => setShowOffcanvas(true)}
          />

          {/* Desktop nav (no collapse; show inline) */}
          <Nav className="ms-auto align-items-lg-center gap-lg-2 d-none d-lg-flex">
            {topLinks.map((l) => {
              if (l.href) {
                return (
                  <Nav.Link
                    key={l.key}
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {l.label}
                  </Nav.Link>
                );
              }

              if (!l.to) {
                return null;
              }

              const linkPath = localePath(l.to);
              const isActive =
                location.pathname === linkPath ||
                (l.to !== "/" && location.pathname.startsWith(linkPath));

              return (
                <Nav.Link
                  key={l.key}
                  as={Link}
                  to={linkPath}
                  className={isActive ? "active" : ""}
                >
                  {l.label}
                </Nav.Link>
              );
            })}
            <LanguageSwitcher />
          </Nav>

          {/* Mobile Offcanvas */}
          <Offcanvas
            id="site-nav-offcanvas"
            placement="end"
            show={showOffcanvas}
            onHide={() => setShowOffcanvas(false)}
            className="d-lg-none"
          >
            <Offcanvas.Header closeButton>
              <Offcanvas.Title>{t("site.name")}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <Nav className="flex-column gap-1">
                {topLinks.map((l) => {
                  if (l.href) {
                    return (
                      <Nav.Link
                        key={l.key}
                        href={l.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {l.label}
                      </Nav.Link>
                    );
                  }

                  if (!l.to) {
                    return null;
                  }

                  const linkPath = localePath(l.to);
                  const isActive =
                    location.pathname === linkPath ||
                    (l.to !== "/" && location.pathname.startsWith(linkPath));

                  return (
                    <Nav.Link
                      key={l.key}
                      as={Link}
                      to={linkPath}
                      className={isActive ? "active" : ""}
                    >
                      {l.label}
                    </Nav.Link>
                  );
                })}

                <div className="mt-3">
                  <LanguageSwitcher />
                </div>
              </Nav>
            </Offcanvas.Body>
          </Offcanvas>
        </Container>
      </Navbar>
    </>
  );
}

export default Navigation;
